// src/features/pdf/pdf-splitter/PdfSplitter.tsx
import React, { useState, useCallback } from 'react';
import {
    Box, Container, Typography, Stack,
    RadioGroup, FormControlLabel, Radio, TextField, FormControl, FormLabel
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import PDFToolWorkspace from '../components/PDFToolWorkspace';
import ToolActionArea, { ToolStatus, OutputFile } from '../components/ToolActionArea';
import PdfViewerModal, { ViewerFile } from '../components/PdfViewerModal'; // Import the new modal
import { splitPdf, downloadFile, downloadFilesAsZip } from '../utils/pdfUtils';
import StyledFAQ from "../components/StyledFAQ";
import {Accept} from "react-dropzone";

// Define FileWithId interface
interface FileWithId {
    id: string;
    file: File;
}

const PdfSplitter: React.FC = () => {
    const { t } = useTranslation();
    const [files, setFiles] = useState<FileWithId[]>([]);
    const [status, setStatus] = useState<ToolStatus>('idle');
    const [outputFiles, setOutputFiles] = useState<OutputFile[]>([]);
    const [error, setError] = useState<string>('');
    const [splitMode, setSplitMode] = useState<'all' | 'custom'>('all');
    const [customRange, setCustomRange] = useState('');

    // State for the PDF viewer
    const [viewerFile, setViewerFile] = useState<ViewerFile | null>(null);

    // Handler to open the PDF viewer
    const handleOpenFileViewer = (file: ViewerFile) => {
        // Log opening file for debugging
        console.log('Opening file in viewer:', file.name);
        setViewerFile(file);
    };

    // Handler to close the PDF viewer
    const handleCloseFileViewer = () => {
        // Log closing viewer
        console.log('Closing file viewer.');
        setViewerFile(null);
    };

    // Reset processing state handler
    const resetProcessingState = useCallback(() => {
        setStatus('idle');
        setOutputFiles([]);
        setError('');
    }, []);

    // Handler for new files from Dropzone
    const handleDrop = useCallback((acceptedFiles: File[]) => {
        const newFile = acceptedFiles[acceptedFiles.length - 1];
        if (newFile) {
            setFiles([{ id: uuidv4(), file: newFile }]);
        }
        resetProcessingState();
    }, [resetProcessingState]);

    const handleDelete = useCallback((fileId: string) => {
        setFiles(currentFiles => currentFiles.filter(f => f.id !== fileId));
    }, []);

    const handleReorder = useCallback((reorderedFiles: FileWithId[]) => {
        setFiles(reorderedFiles);
    }, []);

    const handleSplit = async () => {
        const fileWithId = files[0];
        if (!fileWithId) {
            setError(t('pdf_split_error_no_file', { ns: 'tools' }));
            setStatus('error');
            return;
        }
        if (splitMode === 'custom' && !customRange.trim()) {
            setError(t('pdf_split_error_no_range', { ns: 'tools' }));
            setStatus('error');
            return;
        }

        setStatus('loading');
        setError('');
        try {
            const results = await splitPdf(fileWithId.file, splitMode, customRange);
            setOutputFiles(results);
            setStatus('success');
        } catch (e: any) {
            console.error(e);
            setError(t(e.message || 'pdf_split_error_generic', { ns: 'tools' }));
            setStatus('error');
        }
    };

    // Full Reset handler for reset button
    const handleReset = useCallback(() => {
        setFiles([]);
        resetProcessingState();
        setCustomRange('');
        setSplitMode('all');
    }, [resetProcessingState]);

    const handleDownloadAll = () => {
        if (outputFiles.length > 0 && files[0]) {
            const zipName = `${files[0].file.name.replace(/\.pdf$/i, '') || 'split'}-files.zip`;
            downloadFilesAsZip(outputFiles, zipName);
        }
    };

    const handleDownloadSingle = (fileName: string) => {
        const fileToDownload = outputFiles.find(f => f.name === fileName);
        if (fileToDownload) {
            downloadFile(fileToDownload.data, fileToDownload.name);
        }
    };

    // Handler to preview an output file by name
    const handlePreviewOutput = (fileName: string) => {
        const fileToPreview = outputFiles.find(f => f.name === fileName);
        if(fileToPreview) {
            handleOpenFileViewer(fileToPreview);
        }
    };

    const faqs = [
        { q: 'pdf_split_faq1_q', a: 'pdf_split_faq1_a' },
        { q: 'pdf_split_faq2_q', a: 'pdf_split_faq2_a' },
    ];

    const acceptedFiles: Accept = { 'application/pdf': ['.pdf'] };

    return (
        <>
            <PdfViewerModal
                open={viewerFile !== null}
                onClose={handleCloseFileViewer}
                file={viewerFile}
            />
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Stack spacing={4} alignItems="center">
                    <PDFToolWorkspace
                        titleKey="pdf_split"
                        descKey="pdf_split_desc"
                        acceptedFiles={acceptedFiles}
                        allowMultiple={false}
                        allowReordering={false}
                        disabled={status === 'loading'}
                        files={files}
                        onDrop={handleDrop}
                        onDelete={handleDelete}
                        onReorder={handleReorder}
                        onFileClick={handleOpenFileViewer} // Pass the handler
                    >
                        <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
                            <FormLabel component="legend">{t('pdf_split_options_title', { ns: 'tools' })}</FormLabel>
                            <RadioGroup row value={splitMode} onChange={(e) => setSplitMode(e.target.value as 'all' | 'custom')}>
                                <FormControlLabel value="all" control={<Radio />} label={t('pdf_split_option_all', { ns: 'tools' })} />
                                <FormControlLabel value="custom" control={<Radio />} label={t('pdf_split_option_custom', { ns: 'tools' })} />
                            </RadioGroup>
                            {splitMode === 'custom' && (
                                <TextField
                                    fullWidth
                                    label={t('pdf_split_range_label', { ns: 'tools' })}
                                    placeholder="e.g., 1, 3-5, 8"
                                    value={customRange}
                                    onChange={(e) => setCustomRange(e.target.value)}
                                    variant="outlined"
                                    margin="normal"
                                    size="small"
                                />
                            )}
                        </FormControl>
                    </PDFToolWorkspace>

                    <ToolActionArea
                        status={status}
                        onAction={handleSplit}
                        onReset={handleReset}
                        onDownloadAll={outputFiles.length > 0 ? handleDownloadAll : undefined}
                        onDownloadSingle={handleDownloadSingle}
                        onPreviewOutput={handlePreviewOutput} // Pass the output preview handler
                        actionButtonText={t('pdf_split_action_button', { ns: 'tools' })}
                        successTitle={t('pdf_split_success_title', { ns: 'tools' })}
                        successMessage={t('pdf_split_success_desc', { ns: 'tools' })}
                        errorMessage={error}
                        outputFiles={outputFiles}
                        disabled={files.length === 0 || status === 'loading'}
                    />
                </Stack>

                <Box sx={{ my: 15 }}>
                    <Typography sx={{ mb: 4 }} variant="h4" component="h2" gutterBottom textAlign="center">
                        {t('how_to_split_pdf_title', { ns: 'tools' })}
                    </Typography>
                    <Typography sx={{ mb: 10 }} paragraph color="text.secondary" textAlign="center">
                        {t('how_to_split_pdf_desc', { ns: 'tools' })}
                    </Typography>
                    <StyledFAQ
                        title={t('faq_title', { ns: 'tools' })}
                        faqs={faqs}
                        t={t}
                    />
                </Box>
            </Container>
        </>
    );
};

export default PdfSplitter;